/**
 * The certificates on which the SOAD gives reasons for the treatment being
 * certified, and so may judge that disclosing those reasons would harm the
 * patient. T2 and T4 certify consent and carry no reasons; the "No Certificate"
 * form's reasons are for declining to certify, so there is nothing being
 * withheld from the patient. A new certificate of this kind must be listed here
 * as well as gaining the field.
 */
export const RESTRICTED_DISCLOSURE_CERTIFICATE_IDS = [
    "mha-soad-t3",
    "mha-soad-t5",
    "mha-soad-t6",
    "mha-soad-cto11",
];
function carriesIndicator(form) {
    return RESTRICTED_DISCLOSURE_CERTIFICATE_IDS.includes(form.formTemplate.id);
}
/**
 * One version's own judgement. Certificates signed before the indicator existed
 * hold no value for it, which reads as "not restricted".
 */
export function isVersionRestricted(form, version) {
    return (carriesIndicator(form) && version.data[0]?.data?.disclosureHarmful === true);
}
/**
 * The judgement recorded on a certificate's in-progress draft, which is what
 * the SOAD sees before the version is signed and its part data written.
 */
export function isDraftRestricted(form) {
    return (carriesIndicator(form) &&
        form.allDrafts?.[0]?.data?.disclosureHarmful === true);
}
/**
 * The judgement recorded in one part's data, or null where none was recorded.
 * Part data is stored as JSONB and reaches callers untyped, so this is the one
 * place the field is picked off it.
 */
export function readDisclosureJudgement(part) {
    return part?.data?.disclosureHarmful ?? null;
}
/**
 * The judgement one whole version recorded. Takes the version rather than its
 * first part so the untyped part array is indexed here, where its shape is
 * declared, rather than at each caller.
 */
export function readVersionDisclosureJudgement(version) {
    return readDisclosureJudgement(version?.data[0]);
}
/** Version numbers whose own judgement is "would cause serious harm". */
export function restrictedDisclosureVersions(form) {
    return form.versions
        .filter((version) => isVersionRestricted(form, version))
        .map((version) => version.version);
}
/**
 * Whether the certificate as it currently stands is restricted.
 *
 * Reads the newest version carrying part data rather than the newest version
 * outright: an amendment's version exists from the moment the amendment is
 * started but holds no part data until it is signed, so the newest version is
 * empty for as long as the amendment is open. Reading it would drop the warning
 * for exactly the people the warning is for.
 */
export function currentVersionRestricted(form) {
    const certified = form.versions
        .filter((version) => version.data.length > 0)
        .sort((a, b) => a.version - b.version)
        .at(-1);
    return certified !== undefined && isVersionRestricted(form, certified);
}
//# sourceMappingURL=restrictedDisclosure.js.map